/**
 * Hero deck reservoir construction for the Legendary Arena game engine.
 *
 * buildHeroDeck resolves hero card instances from the registry at setup time,
 * applies the locked rarity → copy-count map (D-13501; 5/3/3/3 = 14 cards per
 * hero across the four-label set { 'Common 1', 'Common 2', 'Uncommon',
 * 'Rare' }), shuffles the flat array via ctx.random.Shuffle, and produces
 * the per-match hero deck reservoir. Sibling pattern with buildVillainDeck
 * (WP-014B): registry walk → flat array → single shuffle → consumed by the
 * orchestrator to populate G.hq (first 5) and G.heroDeck (remainder).
 *
 * Transform partition (D-24468): Transform cards (cards flagged
 * `isTransform`/`transformOf` in a hero's cards[] — the second-form face a
 * base card flips to via [keyword:Transform]) are held OUT of the shuffled
 * reservoir and set aside in the transform side deck (G.transformDeck) by
 * buildTransformSideDeck. heroCardInstanceExtIds still emits every instance
 * (its stats/ability consumers need transform cards); buildHeroDeckCards
 * drops the tagged instances and buildTransformSideDeckCards keeps only them.
 * The side deck is unshuffled — no extra ctx.random call — so the single
 * locked Shuffle envelope in shuffleHeroDeck is preserved.
 *
 * No @legendary-arena/registry import. No boardgame.io import. Setup-time
 * only. Pure helper. The single ctx.random.Shuffle call is the only
 * randomness in this module.
 *
 * Loud-fail surface (D-13501 Option A): when a hero card carries a
 * rarityLabel outside the four-label set, buildHeroDeck throws a
 * full-sentence Error inside Game.setup() (the canonical loud-fail surface
 * per .claude/skills/legendary-game-engine/SKILL.md §Throwing Convention). Cross-set rarity
 * support (e.g., 'Common 3' / 'Uncommon 2' observed in amwp.json) is
 * deferred to the Pending follow-up WP recorded in WORK_INDEX.md.
 */
// ---------------------------------------------------------------------------
// Locked rarity → copy-count map (D-13501)
// ---------------------------------------------------------------------------
// why: D-13501 — canonical Marvel Legendary tabletop rule of 14 cards per
// hero across the four-label rarity set { 'Common 1', 'Common 2',
// 'Uncommon', 'Rare' } yields the 5/3/3/3 split below. Any rarityLabel
// outside the four-label set triggers the Option A loud-fail throw at the
// throw site below — see the deferred follow-up WP placeholder in
// WORK_INDEX.md for cross-set extension (e.g., 'Common 3' / 'Uncommon 2'
// observed in amwp.json). Treat this map literal as the single source of
// truth — never duplicate it in tests or sidecar files.
/**
 * Locked rarity → copy-count map per D-13501.
 *
 * Coverage scope: { 'Common 1': 5, 'Common 2': 3, 'Uncommon': 3, 'Rare': 3 }.
 * Sum = 14 cards per hero across the four-label set.
 */
const RARITY_COPY_COUNT = {
    'Common 1': 5,
    'Common 2': 3,
    Uncommon: 3,
    Rare: 3,
};
/** The four locked rarity labels, in canonical iteration order. */
export const SUPPORTED_RARITY_LABELS = [
    'Common 1',
    'Common 2',
    'Uncommon',
    'Rare',
];
// ---------------------------------------------------------------------------
// Shared cardCounts resolution helpers (WP-137 RS-4 lock)
// ---------------------------------------------------------------------------
// why: D-13701 — cardCounts is the authoritative per-hero copy-count source
// when populated; the locked rarity map (D-13501) is the fallback. The
// helpers are exported so the three setup-time fan-out sites
// (buildHeroDeckCards here, the hero branch of buildCardStats in
// economy.logic.ts, and the hero branch of buildCardDisplayData in
// buildCardDisplayData.ts) resolve copy counts identically by construction.
// Cross-site divergence would cause silent fan-out misses across
// G.cardStats and G.cardDisplayData under specific RNG seeds. The
// shared-helper choice also closes the deferred Phase 7 placeholder
// (D-13703) — promotes data-driven counts over rarity-map extension to
// AMWP-class labels.
/**
 * Builds a per-hero name-keyed copy-count lookup from the registry's
 * cardCounts field on a hero entry.
 *
 * Returns an empty Map when `cardCounts` is absent, null, not a plain
 * object, or contains no valid entries. A value is treated as valid only
 * when ALL three predicates hold:
 *
 *   1. typeof v === 'number'
 *   2. Number.isInteger(v)
 *   3. v >= 1
 *
 * Any other value (0, negative, non-integer float, NaN, string, object,
 * etc.) is silently dropped — the missing key in the returned Map causes
 * the caller to fall through to the rarity-map branch.
 *
 * @param cardCounts - Raw `cardCounts` value from a hero entry; unknown
 *   typed because the structural interfaces accept any shape.
 * @returns A Map of display name → positive integer copy count.
 */
export function buildCardCountsNameLookup(cardCounts) {
    const lookup = new Map();
    if (!cardCounts || typeof cardCounts !== 'object') {
        return lookup;
    }
    const candidate = cardCounts;
    for (const key of Object.keys(candidate)) {
        const value = candidate[key];
        if (typeof value !== 'number')
            continue;
        if (!Number.isInteger(value))
            continue;
        if (value < 1)
            continue;
        lookup.set(key, value);
    }
    return lookup;
}
/**
 * Resolves a hero card's copy count via cardCounts (name-keyed) → rarity
 * map (label-keyed) fallback.
 *
 * Returns the resolved positive integer copy count when either source
 * succeeds; returns `null` when both sources fail. The caller in
 * buildHeroDeckCards throws on `null` (preserving the loud-fail surface
 * inside Game.setup() per D-13501 Option A — softened to require BOTH
 * sources missing per D-13701). Callers in the two fan-out sites
 * (buildCardStats, buildCardDisplayData) treat `null` as silent
 * fall-through because their throw surface is reserved for Game.setup()
 * proper.
 *
 * @param card - Hero card entry; only `name` and `rarityLabel` are read.
 * @param nameLookup - Per-hero map built by buildCardCountsNameLookup.
 * @returns Positive integer copy count, or null when both sources fail.
 */
export function resolveHeroCardCopyCount(card, nameLookup) {
    // why: D-13701 — cardCounts authoritative when present; D-13501 rarity
    // map fallback when absent; D-13703 closes the deferred Phase 7
    // placeholder by promoting data-driven counts over rarity-map extension.
    // Lookup is name-keyed against `card.name` because the upstream patch
    // ships cardCounts keyed by display name (NOT slug); `cardCounts[card.slug]`
    // is always wrong.
    if (typeof card.name === 'string') {
        const fromCardCounts = nameLookup.get(card.name);
        if (typeof fromCardCounts === 'number') {
            return fromCardCounts;
        }
    }
    const fromRarity = RARITY_COPY_COUNT[card.rarityLabel];
    if (typeof fromRarity === 'number') {
        return fromRarity;
    }
    return null;
}
// ---------------------------------------------------------------------------
// parseQualifiedIdForSetup — local duplicate (WP-113 §6 precedent)
// ---------------------------------------------------------------------------
// why: WP-113 §6 step 1 — `import or duplicate locally — author choice`.
// Duplicating locally avoids a circular import between builders and
// matchSetup.validate.ts (per D-10014). Identical rejection rules to the
// validator's parseQualifiedId.
/**
 * Parses a set-qualified ID `<setAbbr>/<slug>` into its components.
 *
 * Returns null on any malformed input — empty string, missing slash,
 * multiple slashes, empty parts, or leading/trailing whitespace.
 */
function parseQualifiedIdForSetup(input) {
    if (typeof input !== 'string' || input.length === 0)
        return null;
    if (input !== input.trim())
        return null;
    const slashIndex = input.indexOf('/');
    if (slashIndex === -1)
        return null;
    if (input.indexOf('/', slashIndex + 1) !== -1)
        return null;
    const setAbbr = input.slice(0, slashIndex);
    const slug = input.slice(slashIndex + 1);
    if (setAbbr.length === 0 || slug.length === 0)
        return null;
    return { setAbbr, slug };
}
// ---------------------------------------------------------------------------
// Runtime type guard
// ---------------------------------------------------------------------------
/**
 * Runtime type guard for RegistryReader.
 *
 * Returns true when the registry object exposes the required methods
 * (getSet). Narrow test mocks that only implement listCards return false,
 * letting buildHeroDeck soft-skip with an empty reservoir (mirrors
 * isVillainDeckRegistryReader / isCardStatsRegistryReader / sibling
 * patterns).
 */
function isRegistryReader(registry) {
    if (!registry || typeof registry !== 'object')
        return false;
    const candidate = registry;
    return typeof candidate.getSet === 'function';
}
// ---------------------------------------------------------------------------
// parsePhysicalCards — defensive structural parser
// ---------------------------------------------------------------------------
/**
 * Parses the raw `physicalCards` field from a hero entry into validated
 * PhysicalCardEntry objects.
 *
 * Returns an empty array when the input is absent, null, not an array,
 * or contains no valid entries. Each entry must have a non-empty string
 * `id`, a positive integer `count`, and a non-empty `sides` array of
 * strings. Malformed entries are silently skipped (defense-in-depth;
 * the registry's Zod validation is the primary gate).
 *
 * @param raw - Raw `physicalCards` value from a hero entry.
 * @returns Array of validated PhysicalCardEntry objects.
 */
function parsePhysicalCards(raw) {
    if (!Array.isArray(raw))
        return [];
    const result = [];
    for (const entry of raw) {
        if (!entry || typeof entry !== 'object')
            continue;
        const candidate = entry;
        if (typeof candidate.id !== 'string' || candidate.id.length === 0)
            continue;
        if (typeof candidate.count !== 'number' || !Number.isInteger(candidate.count) || candidate.count < 1)
            continue;
        if (!Array.isArray(candidate.sides) || candidate.sides.length === 0)
            continue;
        let sidesValid = true;
        for (const side of candidate.sides) {
            if (typeof side !== 'string' || side.length === 0) {
                sidesValid = false;
                break;
            }
        }
        if (!sidesValid)
            continue;
        result.push({
            id: candidate.id,
            count: candidate.count,
            sides: candidate.sides,
        });
    }
    return result;
}
/**
 * Returns the slash-format instance ext_ids for one hero's cards.
 *
 * Reproduces the exact algorithm the hero deck reservoir uses: when
 * `physicalCards` is present (the curated path) it emits `count` ids per
 * physical card using `sides[0]` as the canonical face slug (D-14101); when
 * `physicalCards` is absent it falls back to the per-card
 * `resolveHeroCardCopyCount` cascade (D-13701 → D-13501). Each id is
 * `{setAbbr}/{heroSlug}/{cardSlug}#{copyIndex}` (D-13702).
 *
 * Pure and copyIndex-ascending: it performs no sorting and is safe to call
 * repeatedly. Consumers (this module's deck builder, plus buildCardStats §1b
 * and buildHeroAbilityHooks) use the returned array order directly.
 *
 * Throws the D-13501 Option A loud-fail (full-sentence Error) when, on the
 * fallback path, a card's copy count cannot be resolved from either source —
 * the deck builder's existing loud-fail surface, preserved unchanged. The
 * physicalCards path never throws; malformed physical cards (empty `sides[]`)
 * are soft-skipped by parsePhysicalCards exactly as before.
 *
 * @param setAbbr - Set abbreviation (e.g., "core").
 * @param heroSlug - Hero slug within the set (e.g., "black-widow").
 * @param heroEntry - The hero entry; `cards`, `physicalCards`, and
 *   `cardCounts` are read.
 * @returns Hero card instances in declaration/copy order.
 */
// why: hero per-card lookups must key by the canonical-face slash instance id
// and NOT the dash/slot FlatCard key (D-18705). The zone id
// (`{set}/{heroSlug}/{cardSlug}#{copy}`, slug-based) and the old hook key
// (`{set}-hero-{heroSlug}-{slot}`, slot-based) differed in two independent
// ways (slug-vs-slot and slash-vs-dash), so before WP-191 every hero ability
// lookup silently missed. Centralizing emission here (consumed by the deck
// builder AND the lookup builders) makes the keys agree with the zone +
// cardStats §1b grammar by construction (D-18706; import-not-duplicate).
export function heroCardInstanceExtIds(setAbbr, heroSlug, heroEntry) {
    const instances = [];
    // why: D-24468 — a card is a Transform card when its cards[] entry carries
    // either flag (`isTransform:true` or a `transformOf` base slug). Build the
    // set of transform-card canonical slugs up front so BOTH emission paths
    // (physicalCards + rarity fallback) tag each instance without re-scanning.
    // The tag drives the reservoir/side-deck partition in the two callers; this
    // emitter still emits every instance (stats + ability consumers need them).
    const transformSlugs = new Set();
    for (const card of heroEntry.cards) {
        if (!card || typeof card !== 'object')
            continue;
        if (typeof card.slug !== 'string')
            continue;
        if (card.isTransform === true || typeof card.transformOf === 'string') {
            transformSlugs.add(card.slug);
        }
    }
    // why: D-14101 / D-14102 — when physicalCards is present, emit `count` ids
    // per physical card using sides[0] as the canonical (primary) face slug. Solo
    // heroes (1 side) emit one primary id per copy — unchanged.
    // why: WP-724 / D-24545 — for a split / dual-faced physical card (2 sides), ALSO
    // emit one ALTERNATE-face id per copy (sides[1], isPrimaryFace:false) so both
    // faces' stats/hooks/display are resolvable from G. Only primary faces enter the
    // shuffled reservoir (buildHeroDeckCards filters !isPrimaryFace), so deck
    // composition — one physical copy = one draw — is byte-unchanged; the alternate
    // is bound only when the player chooses it at play time (resolveSplitFaceChoice).
    const physicalCards = parsePhysicalCards(heroEntry.physicalCards);
    if (physicalCards.length > 0) {
        for (const physicalCard of physicalCards) {
            const primarySlug = physicalCard.sides[0];
            const primaryBaseExtId = `${setAbbr}/${heroSlug}/${primarySlug}`;
            const primaryIsTransform = transformSlugs.has(primarySlug);
            // why: sides.length is 1 (solo) or 2 (split), locked by the registry's
            // PhysicalCardSchema `.min(1).max(2)` (D-13802). Read the second side once.
            const alternateSlug = physicalCard.sides.length > 1 ? physicalCard.sides[1] : null;
            const alternateBaseExtId = alternateSlug !== null ? `${setAbbr}/${heroSlug}/${alternateSlug}` : null;
            const alternateIsTransform = alternateSlug !== null ? transformSlugs.has(alternateSlug) : false;
            for (let copyIndex = 0; copyIndex < physicalCard.count; copyIndex++) {
                instances.push({
                    cardSlug: primarySlug,
                    extId: `${primaryBaseExtId}#${copyIndex}`,
                    isTransform: primaryIsTransform,
                    isPrimaryFace: true,
                });
                if (alternateSlug !== null && alternateBaseExtId !== null) {
                    instances.push({
                        cardSlug: alternateSlug,
                        extId: `${alternateBaseExtId}#${copyIndex}`,
                        isTransform: alternateIsTransform,
                        isPrimaryFace: false,
                    });
                }
            }
        }
        return instances;
    }
    // why: fallback to per-card resolveHeroCardCopyCount when physicalCards is
    // absent or empty — preserves backward compatibility with data that has not
    // been curated yet (should not occur after WP-140 Phase 1b, but
    // defense-in-depth). The D-13501 Option A loud-fail lives here so the deck
    // builder's throw-on-unresolvable-copy-count contract is preserved.
    const nameLookup = buildCardCountsNameLookup(heroEntry.cardCounts);
    for (const card of heroEntry.cards) {
        if (!card || typeof card !== 'object')
            continue;
        if (typeof card.slug !== 'string' || typeof card.rarityLabel !== 'string') {
            continue;
        }
        const copyCount = resolveHeroCardCopyCount({ name: card.name, rarityLabel: card.rarityLabel }, nameLookup);
        if (copyCount === null) {
            const supportedList = SUPPORTED_RARITY_LABELS.map((label) => `'${label}'`).join(', ');
            const cardNameDisplay = typeof card.name === 'string' && card.name.length > 0 ? card.name : '<unnamed>';
            throw new Error(`buildHeroDeck refused to build hero '${setAbbr}/${heroSlug}': ` +
                `card '${card.slug}' (display name '${cardNameDisplay}') has no resolvable copy count — ` +
                `the per-hero cardCounts map has no positive integer entry for display name '${cardNameDisplay}', ` +
                `and the card's rarityLabel '${card.rarityLabel}' is not in the supported four-label set ${supportedList}. ` +
                `Either populate cardCounts in the hero entry of data/cards/${setAbbr}.json with a positive integer keyed by '${cardNameDisplay}', ` +
                `or correct the card's rarityLabel to one of the supported labels.`);
        }
        const baseExtId = `${setAbbr}/${heroSlug}/${card.slug}`;
        const isTransform = transformSlugs.has(card.slug);
        for (let copyIndex = 0; copyIndex < copyCount; copyIndex++) {
            instances.push({
                cardSlug: card.slug,
                extId: `${baseExtId}#${copyIndex}`,
                isTransform,
                // why: WP-724 — the rarity-fallback path has no physicalCards, so every
                // card is its own primary face (no split faces reach this branch).
                isPrimaryFace: true,
            });
        }
    }
    return instances;
}
// ---------------------------------------------------------------------------
// buildHeroDeckCards — pure registry walk
// ---------------------------------------------------------------------------
/**
 * Builds the unshuffled flat array of hero card-instance ext_ids.
 *
 * For each hero in heroDeckIds, resolves the hero entry from the set data and
 * delegates instance-id emission to heroCardInstanceExtIds (the single
 * emitter home, D-18706). Card order: heroes per heroDeckIds order; cards per
 * the emitter's order; copies appended in zero-indexed contiguous order.
 *
 * Throws a full-sentence Error (the D-13501 Option A loud-fail surface,
 * raised inside heroCardInstanceExtIds) when, on the fallback path, a card's
 * copy count cannot be resolved from either source.
 *
 * @param heroDeckIds - Array of qualified hero deck IDs `<setAbbr>/<heroSlug>`.
 * @param registry - Setup-time registry reader. Must satisfy RegistryReader.
 * @returns Unshuffled flat array of hero card-instance CardExtIds, each
 *   suffixed with `#<copyIndex>`.
 */
export function buildHeroDeckCards(heroDeckIds, registry) {
    const cards = [];
    for (const heroDeckId of heroDeckIds) {
        const parsed = parseQualifiedIdForSetup(heroDeckId);
        if (parsed === null)
            continue;
        const setData = registry.getSet(parsed.setAbbr);
        if (!setData || typeof setData !== 'object')
            continue;
        const candidate = setData;
        if (!Array.isArray(candidate.heroes))
            continue;
        let heroEntry = null;
        for (const hero of candidate.heroes) {
            if (hero && typeof hero === 'object' && hero.slug === parsed.slug) {
                heroEntry = hero;
                break;
            }
        }
        if (heroEntry === null)
            continue;
        if (!Array.isArray(heroEntry.cards))
            continue;
        const instances = heroCardInstanceExtIds(parsed.setAbbr, parsed.slug, heroEntry);
        for (const instance of instances) {
            // why: D-24468 — transform cards are set aside in G.transformDeck by
            // buildTransformSideDeckCards; they must NOT enter the shuffled hero-deck
            // reservoir (before this partition they were recruited/played as ordinary
            // heroes). The base card stays; only its transform second-form is skipped.
            if (instance.isTransform)
                continue;
            // why: WP-724 / D-24545 — a split physical card contributes exactly ONE draw
            // per copy (one physical card), so only its PRIMARY face (sides[0]) enters the
            // reservoir; the alternate face (sides[1]) is resolvable from G.cardStats /
            // heroAbilityHooks / cardDisplayData but is never drawn on its own — it is
            // bound only when the player chooses that side at play time. Keeping only
            // primary faces here makes the reservoir byte-identical to before this WP.
            if (!instance.isPrimaryFace)
                continue;
            cards.push(instance.extId);
        }
    }
    return cards;
}
// ---------------------------------------------------------------------------
// buildTransformSideDeckCards — transform side-deck reservoir (D-24468)
// ---------------------------------------------------------------------------
/**
 * Builds the unshuffled flat array of Transform card-instance ext_ids — the
 * complement of buildHeroDeckCards over the same hero set.
 *
 * Walks the identical registry path as buildHeroDeckCards and keeps ONLY the
 * instances heroCardInstanceExtIds tagged `isTransform` (cards flagged
 * `isTransform`/`transformOf` in the hero's cards[]). Order matches the
 * emitter's declaration/copy order; NO shuffle is applied — every copy of a
 * transform card is identical, so the side deck needs no randomness and the
 * single locked hero-deck Shuffle envelope (shuffleHeroDeck) is preserved.
 *
 * Returns an empty array when no hero in heroDeckIds has transform cards (the
 * common case today — only wwhk heroes carry them). Malformed ids / missing
 * heroes are soft-skipped identically to buildHeroDeckCards.
 *
 * @param heroDeckIds - Array of qualified hero deck IDs `<setAbbr>/<heroSlug>`.
 * @param registry - Setup-time registry reader. Must satisfy RegistryReader.
 * @returns Unshuffled flat array of transform card-instance CardExtIds.
 */
export function buildTransformSideDeckCards(heroDeckIds, registry) {
    const cards = [];
    for (const heroDeckId of heroDeckIds) {
        const parsed = parseQualifiedIdForSetup(heroDeckId);
        if (parsed === null)
            continue;
        const setData = registry.getSet(parsed.setAbbr);
        if (!setData || typeof setData !== 'object')
            continue;
        const candidate = setData;
        if (!Array.isArray(candidate.heroes))
            continue;
        let heroEntry = null;
        for (const hero of candidate.heroes) {
            if (hero && typeof hero === 'object' && hero.slug === parsed.slug) {
                heroEntry = hero;
                break;
            }
        }
        if (heroEntry === null)
            continue;
        if (!Array.isArray(heroEntry.cards))
            continue;
        const instances = heroCardInstanceExtIds(parsed.setAbbr, parsed.slug, heroEntry);
        for (const instance of instances) {
            if (!instance.isTransform)
                continue;
            cards.push(instance.extId);
        }
    }
    return cards;
}
// ---------------------------------------------------------------------------
// shuffleHeroDeck — single ctx.random.Shuffle call site
// ---------------------------------------------------------------------------
/**
 * Returns a new shuffled copy of the supplied hero card flat array using
 * the deterministic RNG provided by boardgame.io.
 *
 * Mirrors playerInit.ts:shuffleDeck — defensive copy of the input array
 * is passed to context.random.Shuffle so the caller's array is never
 * mutated.
 *
 * @param cards - Unshuffled flat array of hero card-instance ext_ids.
 * @param context - Any context that provides random.Shuffle.
 * @returns A new shuffled array; the input is never mutated.
 */
export function shuffleHeroDeck(cards, context) {
    // why: WP-135 determinism guarantee — the single ctx.random.Shuffle call
    // for the hero deck reservoir lives here. Per-turn reshuffle, batched
    // pop, and deck reorder are forbidden; widening this envelope breaks
    // replay determinism. The replay-hash regression guard is the canary.
    return context.random.Shuffle([...cards]);
}
// ---------------------------------------------------------------------------
// buildHeroDeck — canonical entry point
// ---------------------------------------------------------------------------
/**
 * Builds the deterministic per-match hero deck reservoir.
 *
 * Composes buildHeroDeckCards (registry walk + rarity-map expansion) and
 * shuffleHeroDeck (single ctx.random.Shuffle call) into the canonical
 * setup-time entry point. The orchestrator (buildInitialGameState) calls
 * this exactly once per match; the first 5 cards populate G.hq via
 * fillHqFromDeck, and the remainder is stored at G.heroDeck.
 *
 * Soft-skip on incomplete RegistryReader interface (mirrors
 * buildVillainDeck — narrow test mocks return an empty reservoir
 * gracefully). Loud-fail (Option A) on unknown rarityLabel inside
 * buildHeroDeckCards.
 *
 * @param heroDeckIds - Array of qualified hero deck IDs `<setAbbr>/<heroSlug>`.
 * @param registry - Setup-time registry reader. Accepts unknown to support
 *   narrow test mocks. When the registry does not satisfy RegistryReader
 *   structurally (missing getSet), returns an empty array gracefully.
 * @param context - Setup context with ctx.random.Shuffle.
 * @returns A new shuffled hero deck reservoir as CardExtId[].
 * @throws {Error} D-13501 Option A loud-fail — when any hero card carries
 *   a rarityLabel outside the four-label set.
 */
export function buildHeroDeck(heroDeckIds, registry, context) {
    if (!isRegistryReader(registry)) {
        return [];
    }
    const unshuffled = buildHeroDeckCards(heroDeckIds, registry);
    return shuffleHeroDeck(unshuffled, context);
}
// ---------------------------------------------------------------------------
// buildTransformSideDeck — canonical entry point for the transform side deck
// ---------------------------------------------------------------------------
/**
 * Builds the deterministic per-match transform side deck (G.transformDeck).
 *
 * The setup-time sibling of buildHeroDeck: same hero set, complementary
 * partition. buildHeroDeck returns the shuffled reservoir WITHOUT transform
 * cards; this returns the transform cards that were held back (D-24468),
 * unshuffled — no ctx.random call, so the single locked hero-deck Shuffle
 * envelope is untouched. The orchestrator (buildInitialGameState) calls this
 * exactly once per match and stores the result at G.transformDeck.
 *
 * Soft-skip on incomplete RegistryReader interface (mirrors buildHeroDeck —
 * narrow test mocks return an empty side deck gracefully).
 *
 * @param heroDeckIds - Array of qualified hero deck IDs `<setAbbr>/<heroSlug>`.
 * @param registry - Setup-time registry reader. Accepts unknown to support
 *   narrow test mocks; returns [] when it does not satisfy RegistryReader.
 * @returns The unshuffled transform side deck as CardExtId[].
 */
export function buildTransformSideDeck(heroDeckIds, registry) {
    if (!isRegistryReader(registry)) {
        return [];
    }
    return buildTransformSideDeckCards(heroDeckIds, registry);
}
// ---------------------------------------------------------------------------
// buildTransformTargets — base→second-form card-key map (D-24469)
// ---------------------------------------------------------------------------
/**
 * Builds the per-match base→second-form card-key map (G.transformTargets).
 *
 * Walks the same registry path as buildTransformSideDeckCards over the SAME
 * hero set, but instead of collecting card instances it records, for every
 * BASE card that carries a `transform` field, a copy-agnostic card-key entry:
 *
 *   `{setAbbr}/{heroSlug}/{baseSlug}` → `{setAbbr}/{heroSlug}/{targetSlug}`
 *
 * (e.g. `wwhk/she-hulk/hurl-legal-objections` → `wwhk/she-hulk/hurl-trucks`).
 * One entry per base card — every copy of that base shares the key, so
 * heroEffectTransform strips the `#copy` suffix off the played base ext_id to
 * look the target key up, then pulls the first `{targetKey}#*` instance out of
 * G.transformDeck. The map is built for EVERY wwhk base→target pair (complete
 * data); which base cards actually resolve a swap is gated separately at the
 * setup parser (SUPPORTED_TRANSFORM_BASES).
 *
 * Returns an empty object when no hero in heroDeckIds has transform base cards
 * (the common case today — only wwhk heroes carry them). Malformed ids /
 * missing heroes are soft-skipped identically to buildTransformSideDeckCards.
 * No ctx.random, no I/O — pure setup-time registry walk.
 *
 * @param heroDeckIds - Array of qualified hero deck IDs `<setAbbr>/<heroSlug>`.
 * @param registry - Setup-time registry reader. Accepts unknown to support
 *   narrow test mocks; returns {} when it does not satisfy RegistryReader.
 * @returns Base-key → target-key map as Record<CardExtId, CardExtId>.
 */
export function buildTransformTargets(heroDeckIds, registry) {
    const targets = {};
    if (!isRegistryReader(registry)) {
        return targets;
    }
    for (const heroDeckId of heroDeckIds) {
        const parsed = parseQualifiedIdForSetup(heroDeckId);
        if (parsed === null)
            continue;
        const setData = registry.getSet(parsed.setAbbr);
        if (!setData || typeof setData !== 'object')
            continue;
        const candidate = setData;
        if (!Array.isArray(candidate.heroes))
            continue;
        let heroEntry = null;
        for (const hero of candidate.heroes) {
            if (hero && typeof hero === 'object' && hero.slug === parsed.slug) {
                heroEntry = hero;
                break;
            }
        }
        if (heroEntry === null)
            continue;
        if (!Array.isArray(heroEntry.cards))
            continue;
        for (const card of heroEntry.cards) {
            // why: only BASE cards carry `transform` (the second-form slug); the
            // second-form cards carry `transformOf` instead. A blank/malformed value
            // is soft-skipped (no throw) — the map is defence-in-depth over data the
            // cards:check gate already validates.
            if (typeof card.slug !== 'string' || card.slug.length === 0)
                continue;
            if (typeof card.transform !== 'string' || card.transform.length === 0)
                continue;
            const baseKey = `${parsed.setAbbr}/${parsed.slug}/${card.slug}`;
            const targetKey = `${parsed.setAbbr}/${parsed.slug}/${card.transform}`;
            targets[baseKey] = targetKey;
        }
    }
    return targets;
}
// ---------------------------------------------------------------------------
// buildSplitFaces — split-hero primary→alternate face-key map (D-24545)
// ---------------------------------------------------------------------------
/**
 * Builds the per-match primary→alternate face-key map (G.splitFaces) for split /
 * dual-faced hero cards (WP-724 / D-24545, un-deferring D-14101).
 *
 * Walks the same registry path as buildTransformTargets over the SAME hero set,
 * but records, for every physical card with two sides, a copy-agnostic card-key
 * entry mapping the PRIMARY face to the ALTERNATE face:
 *
 *   `{setAbbr}/{heroSlug}/{sides[0]}` → `{setAbbr}/{heroSlug}/{sides[1]}`
 *
 * (e.g. `cvwr/peter-parker/hot-bowl-of-soup` → `cvwr/peter-parker/protect-my-family`).
 * One entry per split physical card — every copy shares the key, so playCard strips
 * the `#copy` suffix off the played primary-face instance to look the alternate base
 * up, then reattaches the same `#copy` to offer faceB. Solo cards (one side) are not
 * recorded — the map holds only split cards.
 *
 * Returns an empty object when no hero in heroDeckIds has split cards (the core
 * sentinel and most sets). The caller (buildInitialGameState) omits the field from `G`
 * entirely when the map is empty, so a no-split game serializes byte-identically and no
 * hash oracle re-pins (contrast buildTransformTargets, which is always seeded). No
 * ctx.random, no I/O — pure setup-time registry walk. Malformed ids / missing heroes are
 * soft-skipped identically to buildTransformTargets.
 *
 * @param heroDeckIds - Array of qualified hero deck IDs `<setAbbr>/<heroSlug>`.
 * @param registry - Setup-time registry reader. Accepts unknown to support narrow test
 *   mocks; returns {} when it does not satisfy RegistryReader.
 * @returns Primary-face-key → alternate-face-key map as Record<CardExtId, CardExtId>.
 */
export function buildSplitFaces(heroDeckIds, registry) {
    const splitFaces = {};
    if (!isRegistryReader(registry)) {
        return splitFaces;
    }
    for (const heroDeckId of heroDeckIds) {
        const parsed = parseQualifiedIdForSetup(heroDeckId);
        if (parsed === null)
            continue;
        const setData = registry.getSet(parsed.setAbbr);
        if (!setData || typeof setData !== 'object')
            continue;
        const candidate = setData;
        if (!Array.isArray(candidate.heroes))
            continue;
        let heroEntry = null;
        for (const hero of candidate.heroes) {
            if (hero && typeof hero === 'object' && hero.slug === parsed.slug) {
                heroEntry = hero;
                break;
            }
        }
        if (heroEntry === null)
            continue;
        const physicalCards = parsePhysicalCards(heroEntry.physicalCards);
        for (const physicalCard of physicalCards) {
            // why: only 2-sided physical cards are split; solo cards contribute nothing.
            if (physicalCard.sides.length < 2)
                continue;
            const primaryKey = `${parsed.setAbbr}/${parsed.slug}/${physicalCard.sides[0]}`;
            const alternateKey = `${parsed.setAbbr}/${parsed.slug}/${physicalCard.sides[1]}`;
            splitFaces[primaryKey] = alternateKey;
        }
    }
    return splitFaces;
}
/**
 * Builds the set of split cards whose ALTERNATE face (`sides[1]`) is the printed LEFT
 * half of the landscape card (G.splitFacesAlternateOnLeft).
 *
 * `sides[]` order is the engine's primary/alternate order, NOT the printed order: the
 * printed position is the hero-card `slot` — the face with the LOWER slot is the left
 * half. Records the copy-agnostic PRIMARY card-key (the splitFaces key) for every split
 * physical card whose sides[1] slot is lower than its sides[0] slot. A pair with a
 * missing slot on either face is soft-skipped (treated as primary-on-left, the pre-fix
 * render order). Display-only: consumed by buildUIState to order the "choose a side"
 * picker; never read by a move.
 *
 * Returns an empty object when no split card is reversed; the caller omits the field
 * from `G` then. No ctx.random, no I/O — pure setup-time registry walk mirroring
 * buildSplitFaces.
 *
 * @param heroDeckIds - Array of qualified hero deck IDs `<setAbbr>/<heroSlug>`.
 * @param registry - Setup-time registry reader. Accepts unknown to support narrow test
 *   mocks; returns {} when it does not satisfy RegistryReader.
 * @returns Primary-face-key → true for each split card printed alternate-face-left.
 */
export function buildSplitFaceAlternateOnLeft(heroDeckIds, registry) {
    const alternateOnLeft = {};
    if (!isRegistryReader(registry)) {
        return alternateOnLeft;
    }
    for (const heroDeckId of heroDeckIds) {
        const parsed = parseQualifiedIdForSetup(heroDeckId);
        if (parsed === null)
            continue;
        const setData = registry.getSet(parsed.setAbbr);
        if (!setData || typeof setData !== 'object')
            continue;
        const candidate = setData;
        if (!Array.isArray(candidate.heroes))
            continue;
        let heroEntry = null;
        for (const hero of candidate.heroes) {
            if (hero && typeof hero === 'object' && hero.slug === parsed.slug) {
                heroEntry = hero;
                break;
            }
        }
        if (heroEntry === null)
            continue;
        if (!Array.isArray(heroEntry.cards))
            continue;
        const slotBySlug = {};
        for (const card of heroEntry.cards) {
            if (typeof card.slug === 'string' && typeof card.slot === 'number') {
                slotBySlug[card.slug] = card.slot;
            }
        }
        const physicalCards = parsePhysicalCards(heroEntry.physicalCards);
        for (const physicalCard of physicalCards) {
            if (physicalCard.sides.length < 2)
                continue;
            const primarySlot = slotBySlug[physicalCard.sides[0]];
            const alternateSlot = slotBySlug[physicalCard.sides[1]];
            if (primarySlot === undefined || alternateSlot === undefined)
                continue;
            if (alternateSlot < primarySlot) {
                const primaryKey = `${parsed.setAbbr}/${parsed.slug}/${physicalCard.sides[0]}`;
                alternateOnLeft[primaryKey] = true;
            }
        }
    }
    return alternateOnLeft;
}
//# sourceMappingURL=buildHeroDeck.js.map